import type { GhostAppearance } from '../../../shared/types/AppearanceTypes';
import { JobClass, type Helix2000Character, type CharacterClass } from '../../../shared/CharacterTypes';
import { CLASS_CONFIGS } from '../../../shared/classes/ClassConfigs';
import type { PeriodicSyncPayload, PlayerSchemaView } from '../../network/ClientMessageTypes';
import type { ServerHotbarSnapshotAuthority } from '../../systems/player/PlayerManager';

export function projectPlayerSchema(
  prev: Helix2000Character | null,
  playerSchema: PlayerSchemaView,
  activeHotbarAuthorityRef: { readonly current: ServerHotbarSnapshotAuthority | null },
  activeHeavyFieldsSessionRef: { readonly current: string | null },
): Helix2000Character | null {
  if (!prev) return null;
  // GM-override flags (infiniteMana/gcdOverride/noRangeGate) aren't on the
  // Helix2000Character type — widen `next` to carry them. // typed-later
  const next = { ...prev } as typeof prev & {
    peaceful?: boolean;
    infiniteMana?: boolean;
    gcdOverride?: number;
    noRangeGate?: boolean;
  };

  // Sync Stats
  next.stats = {
    ...next.stats,
    strength: playerSchema.strength, dexterity: playerSchema.dexterity,
    intelligence: playerSchema.intelligence, luck: playerSchema.luck,
    hp: playerSchema.health, mp: playerSchema.mana,
    maxHp: playerSchema.maxHealth, maxMp: playerSchema.maxMana,
    attack: playerSchema.attack, magicAttack: playerSchema.magicAttack,
    defense: playerSchema.defense, magicDefense: playerSchema.magicDefense,
    criticalRate: playerSchema.criticalRate, criticalDamage: playerSchema.criticalDamage,
    accuracy: playerSchema.accuracy, avoidability: playerSchema.avoidability,
    speed: playerSchema.speed, jump: playerSchema.jump
  };

  // Sync Vitals & Resources
  next.health = playerSchema.health;
  next.maxHealth = playerSchema.maxHealth;
  next.mana = playerSchema.mana;
  next.maxMana = playerSchema.maxMana;
  next.level = playerSchema.level;
  next.xp = playerSchema.xp;
  next.statPoints = playerSchema.statPoints;
  // Rejoin schema restores the job when the selected character snapshot is stale.
  const job = playerSchema.job;
  if (typeof job === 'string' && Object.values(JobClass).includes(job as JobClass)) {
    next.job = job as JobClass;
  }
  // Position is NOT projected (plan 13 §8). Client-owned movement makes the
  // PhysicsEntity the source of truth, and the own-player schema patches x/y
  // on every server tick while walking: projecting it built a new `player`
  // object per patch and re-rendered the whole `Game` tree ~30×/s. React's
  // `player.position` keeps the load / teleport / correction value; code that
  // needs the live position reads `livePlayerPosition()`
  // (src/game/systems/player/livePlayerPosition.ts) when it needs it.
  // `next = { ...prev }` carries prev.position by reference — React-owned
  // since LRG.21, never the PhysicsEntity's object.
  next.inventory = { ...prev.inventory, gold: playerSchema.gold };
  next.gmLevel = playerSchema.gmLevel ?? 0;
  const hotbarAuthority = activeHotbarAuthorityRef.current;
  next.peaceful = hotbarAuthority?.sessionId === activeHeavyFieldsSessionRef.current
    ? hotbarAuthority.peaceful
    : playerSchema.peaceful ?? false;
  next.infiniteMana = playerSchema.infiniteMana ?? false;
  next.gcdOverride = playerSchema.gcdOverride ?? -1;
  next.noRangeGate = playerSchema.noRangeGate ?? false;

  // BIG-EQUIP-VIS-01 S6 — sync the server-authoritative appearance so
  // the local player object used by the renderer matches the remote view.
  if (playerSchema.appearance) {
    next.appearance = playerSchema.appearance as unknown as GhostAppearance;
  }

  // Apply Movement Stats
  const classConfig = CLASS_CONFIGS[next.class as CharacterClass];
  if (classConfig && classConfig.movementStats) {
    const speedMult = (playerSchema.speed || 100) / 100;
    const jumpMult = (playerSchema.jump || 100) / 100;

    next.movementStats = {
      ...next.movementStats,
      moveSpeed: classConfig.movementStats.moveSpeed * speedMult,
      jumpPower: classConfig.movementStats.jumpPower * jumpMult
    };
  }

  // Phase 3 of WS 1009 state-bloat arc (2026-05-16):
  // inventory/hotbar/quests/completedQuests are no longer on the
  // synced wire — they arrive via dedicated direct-message
  // channels (inventoryUpdate / hotbarUpdate / questsUpdate /
  // completedQuestsUpdate / heavyFieldsSnapshot). The
  // useColyseusMessages binding below pumps them into React state.

  return sameProjection(prev, next) ? prev : next;
}

/**
 * Current HP/MP. The server regenerates them by a fraction of a point on every
 * tick, so they live in the vitals store (`livePlayerVitals`, written by the
 * SERVER_STATE_UPDATE handler next to this projection) and never decide
 * whether `player` changed: a regen or damage patch keeps `prev`, so `Game`,
 * the HUD and the hotbar don't re-render for it (plan 21 §8.3). Their maxima
 * stay ordinary fields.
 */
const TICK_VITALS = new Set(['health', 'mana', 'hp', 'mp']);
/** Nested objects the projection rebuilds on every call. */
const REBUILT_OBJECTS = new Set(['stats', 'inventory', 'movementStats']);

function sameValue(key: string, a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return TICK_VITALS.has(key) && typeof a === 'number' && typeof b === 'number';
}

/**
 * True when `next` shows nothing `prev` doesn't: the same top-level values,
 * with the rebuilt nested objects compared field by field and current HP/MP
 * ignored (they live in the vitals store).
 */
export function sameProjection(prev: object, next: object): boolean {
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  for (const key of Object.keys(n)) {
    const a = p[key];
    const b = n[key];
    if (sameValue(key, a, b)) continue;
    if (!REBUILT_OBJECTS.has(key) || !a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
    for (const k of keys) {
      if (!sameValue(k, ao[k], bo[k])) return false;
    }
  }
  return Object.keys(p).length === Object.keys(n).length;
}

type GmFlags = { peaceful?: boolean; gcdOverride?: number; infiniteMana?: boolean; noRangeGate?: boolean };
/** Fields `net:periodicSync` projects that decide whether `player` changed (current HP/MP don't). */
const PERIODIC_FIELDS = [
  'xp', 'level', 'statPoints', 'maxHealth', 'maxMana', 'peaceful', 'gcdOverride', 'infiniteMana', 'noRangeGate',
] as const;

/**
 * The 5 s `net:periodicSync` snapshot onto the React player. Its current HP/MP
 * go to the vitals store (the caller writes them); here they ride along
 * only when something else changed, like `projectPlayerSchema`.
 */
export function projectPeriodicSync(
  prev: Helix2000Character | null,
  data: PeriodicSyncPayload,
  activeHotbarAuthorityRef: { readonly current: ServerHotbarSnapshotAuthority | null },
  activeHeavyFieldsSessionRef: { readonly current: string | null },
): Helix2000Character | null {
  if (!prev) return null;
  const current = prev as Helix2000Character & GmFlags;
  const authority = activeHotbarAuthorityRef.current;
  const next: Helix2000Character & GmFlags = {
    ...prev,
    xp: data.xp ?? prev.xp,
    level: data.level ?? prev.level,
    statPoints: data.statPoints ?? prev.statPoints,
    health: data.health ?? prev.health,
    maxHealth: data.maxHealth ?? prev.maxHealth,
    mana: data.mana ?? prev.mana,
    maxMana: data.maxMana ?? prev.maxMana,
    peaceful: authority?.sessionId === activeHeavyFieldsSessionRef.current
      ? authority!.peaceful
      : data.peaceful ?? current.peaceful,
    gcdOverride: data.gcdOverride ?? current.gcdOverride,
    infiniteMana: data.infiniteMana ?? current.infiniteMana,
    noRangeGate: data.noRangeGate ?? current.noRangeGate,
  };
  return PERIODIC_FIELDS.every(key => next[key] === current[key]) ? prev : next;
}

/** Fields the 1 Hz heavy-fields snapshot rebuilds on every delivery. */
const HEAVY_FIELDS = ['inventory', 'actionBar', 'quests', 'knownPlaces', 'claimedRewards'] as const;

/**
 * The heavy-fields snapshot repeats at 1 Hz and rebuilds the inventory, hotbar
 * and quests objects each time, so an unchanged snapshot made a new `player`
 * and re-rendered `Game` (with the HUD and the hotbar) once a second on every
 * map (B7, lane AB 2026-10-08). Keep `prev`'s object for each field whose
 * content is unchanged, and `prev` itself when nothing changed.
 */
export function keepUnchangedHeavyFields<T extends object>(prev: T, next: T): T {
  const p = prev as Record<string, unknown>;
  const n = next as Record<string, unknown>;
  for (const key of HEAVY_FIELDS) {
    if (n[key] !== p[key] && JSON.stringify(n[key]) === JSON.stringify(p[key])) n[key] = p[key];
  }
  for (const key of new Set([...Object.keys(p), ...Object.keys(n)])) {
    if (p[key] !== n[key]) return next;
  }
  return prev;
}
